import { Button } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { LocaleSwitch } from "@/client/components/locale-switch";
import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useAuthStore } from "@/client/stores/auth";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Scrolls to a home section. When we are on another page, navigate to the
  // home page with the section hash so the target still works.
  const scrollToSection = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMenuOpen) setIsMenuOpen(false);

    if (pathname === "/") {
      document.querySelector(`#${sectionId}`)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <motion.header
      className="fixed top-0 z-50 w-full border-b border-border/40 backdrop-blur-xl dark:bg-transparent"
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.3 } }}
    >
      <div className="container py-4">
        {/* Layout flexible pour s'adapter au mobile et desktop */}
        <div className="flex w-full items-center justify-between md:grid md:grid-cols-[1fr_auto_1fr]">
          {/* Logo - Left */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <Logo size={100} />
            </Link>
          </div>

          {/* Desktop Navigation Links - Center */}
          <nav className="hidden items-center justify-center md:flex">
            <div className="flex items-center space-x-8">
              <div className="group relative text-base">
                <button className="flex items-center gap-1 py-2 font-medium text-foreground/80 transition-colors hover:text-foreground">
                  {t`Features`}
                  <ChevronDown className="size-4 opacity-60" />
                </button>

                <div className="invisible absolute left-0 top-full z-50 w-56 translate-y-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="mt-2 overflow-hidden rounded-xl border border-border bg-background shadow-lg">
                    <div className="p-2 text-sm">
                      <a
                        href="#features"
                        className="block rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        onClick={scrollToSection("features")}
                      >
                        {t`ATS Optimization`}
                      </a>
                      <a
                        href="#features"
                        className="block rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        onClick={scrollToSection("features")}
                      >
                        {t`AI Writing`}
                      </a>
                      <a
                        href="#features"
                        className="block rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        onClick={scrollToSection("features")}
                      >
                        {t`Smart Templates`}
                      </a>
                      <a
                        href="#features"
                        className="block rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        onClick={scrollToSection("features")}
                      >
                        {t`Resume Analytics`}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="#templates"
                className="text-base font-medium text-foreground/80 transition-colors hover:text-foreground"
                onClick={scrollToSection("templates")}
              >
                {t`Templates`}
              </a>

              <Link
                to="/pricing"
                className="text-base font-medium text-foreground/80 transition-colors hover:text-foreground"
              >
                {t`Pricing`}
              </Link>

              <a
                href="/#how-it-works"
                className="text-base font-medium text-foreground/80 transition-colors hover:text-foreground"
                onClick={scrollToSection("how-it-works")}
              >
                {t`How it works?`}
              </a>
              <a
                href="#faq"
                className="text-base font-medium text-foreground/80 transition-colors hover:text-foreground"
                onClick={scrollToSection("faq")}
              >
                {t`FAQ`}
              </a>
            </div>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center justify-end space-x-3">
            <div className="flex items-center space-x-2">
              <LocaleSwitch />
              <ThemeSwitch />
            </div>

            {isLoggedIn ? (
              <Button asChild size="sm" className="hidden sm:flex">
                <Link to="/dashboard">{t`Dashboard`}</Link>
              </Button>
            ) : (
              <Button asChild size="sm" className="hidden sm:flex">
                <Link to="/auth/register">{t`Create a Resume`}</Link>
              </Button>
            )}

            <button
              className="flex p-2 md:hidden"
              aria-label={t`Toggle navigation menu`}
              onClick={toggleMenu}
            >
              {isMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-x-0 top-[65px] z-40 border-b border-border bg-background p-4 md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col space-y-3">
              <div className="pb-2 pt-1">
                <p className="mb-3 font-medium uppercase tracking-wider text-foreground/60">
                  {t`Features`}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href="#features"
                    className="flex flex-col gap-1 rounded-lg border border-border/60 p-3 hover:border-border hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
                    onClick={scrollToSection("features")}
                  >
                    <span className="font-medium">{t`ATS Optimization`}</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      {t`Pass automated filters`}
                    </span>
                  </a>
                  <a
                    href="#features"
                    className="flex flex-col gap-1 rounded-lg border border-border/60 p-3 hover:border-border hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
                    onClick={scrollToSection("features")}
                  >
                    <span className="font-medium">{t`AI Writing`}</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">{t`Impact phrasing`}</span>
                  </a>
                  <a
                    href="#features"
                    className="flex flex-col gap-1 rounded-lg border border-border/60 p-3 hover:border-border hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
                    onClick={scrollToSection("features")}
                  >
                    <span className="font-medium">{t`Smart Templates`}</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">{t`HR-approved design`}</span>
                  </a>
                  <a
                    href="#features"
                    className="flex flex-col gap-1 rounded-lg border border-border/60 p-3 hover:border-border hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
                    onClick={scrollToSection("features")}
                  >
                    <span className="font-medium">{t`Resume Analytics`}</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">{t`Track your performance`}</span>
                  </a>
                </div>
              </div>

              <a
                href="#templates"
                className="border-t border-border/30 py-2 text-base"
                onClick={scrollToSection("templates")}
              >
                {t`Templates`}
              </a>
              <Link
                to="/pricing"
                className="border-t border-border/30 py-2 text-base"
                onClick={() => {
                  setIsMenuOpen(false);
                }}
              >
                {t`Pricing`}
              </Link>

              <div className="flex flex-col space-y-3 border-t border-border/30 pb-2 pt-4">
                <Button asChild variant="outline" size="md">
                  <Link to="/auth/login">{t`Login`}</Link>
                </Button>

                <Button asChild size="md">
                  <Link to="/auth/register">{t`Create a Resume`}</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
